import * as __WEBPACK_EXTERNAL_MODULE_deepmerge__ from "deepmerge";
import * as __WEBPACK_EXTERNAL_MODULE_json_stringify_safe_eb014bbd__ from "json-stringify-safe";
import * as __WEBPACK_EXTERNAL_MODULE__hh_ru_browser_api_utils_8f48670e__ from "@hh.ru/browser-api-utils";
import * as __WEBPACK_EXTERNAL_MODULE__data_generatedBrowserVersionsForSentryLogging_mjs_51430925__ from "../data/generatedBrowserVersionsForSentryLogging.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__pushAxiosDataToSentry_mjs_7c7579fb__ from "../pushAxiosDataToSentry.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__removeData_mjs_1c816fc9__ from "../removeData.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__defaultConfig_mjs_d5104688__ from "./defaultConfig.mjs";
const MAX_LAST_ACTION_PAYLOAD_LENGTH = __WEBPACK_EXTERNAL_MODULE__removeData_mjs_1c816fc9__.MAX_EXTRA_DATA_LENGTH / 20;
const NOT_DETECTED_TAG_VALUE = 'not_detected';
const ALREADY_HANDLED_FLAG = '_alreadyHandledByHHSentryJs';
const isBrowserSupported = ()=>{
    const userAgentDetails = (0, __WEBPACK_EXTERNAL_MODULE__hh_ru_browser_api_utils_8f48670e__.getUserAgentDetails)(window.navigator.userAgent, __WEBPACK_EXTERNAL_MODULE__data_generatedBrowserVersionsForSentryLogging_mjs_51430925__["default"]);
    return !!(userAgentDetails.isSupported && userAgentDetails.browser);
};
const getEnvironment = ()=>{
    const re = window.navigator.userAgent.match(/test_host=(\w+)/);
    const isAutotests = !!re;
    const standName = re?.[1] || null;
    return {
        isAutotests,
        standName
    };
};
const parseSentryExternalConfig = (config)=>{
    if (!config) return null;
    try {
        return JSON.parse(config);
    } catch (e) {
        console.error('Sentry config could not be parsed', e);
        return null;
    }
};
const getIgnoreConfig = (type, externalConfig)=>{
    if (!externalConfig) return [];
    const ignoreConfig = externalConfig[type];
    if (!ignoreConfig) return [];
    const { strings = [], regexps = [] } = ignoreConfig;
    if (!strings.length && !regexps.length) return [];
    const filterRegexp = (regexp)=>!!regexp;
    const regexpObjects = regexps.map((regexp)=>{
        try {
            if ('string' == typeof regexp) return new RegExp(regexp, 'i');
            return new RegExp(regexp.pattern, regexp.flags);
        } catch (e) {
            console.error(e);
            return null;
        }
    }).filter(filterRegexp);
    return [
        ...strings,
        ...regexpObjects
    ];
};
const setFramesInAppFlag = (frames = [])=>{
    const vendorFileParts = [
        'vendors.',
        'magritte/',
        'bloko/',
        '.yarn/',
        'webpack'
    ];
    frames.forEach((frame)=>frame.in_app = !vendorFileParts.some((part)=>frame.filename?.includes(part)));
};
const isErrorInCache = (errorMessage)=>{
    const ERRORS_CACHE_KEY = '__jsErrorsCache';
    const ERRORS_CACHE_STORING_DURATION_MS = 86400000;
    let errors;
    try {
        errors = JSON.parse(window.localStorage.getItem(ERRORS_CACHE_KEY));
    } catch (_) {
        errors = [];
    }
    if (!('isArray' in Array && Array.isArray(errors))) errors = [];
    const currentDate = Date.now();
    const relevantErrors = errors.filter((cacheError)=>cacheError.date + ERRORS_CACHE_STORING_DURATION_MS >= currentDate);
    const inCache = relevantErrors.some((errorCache)=>errorCache.message === errorMessage);
    if (!inCache) {
        relevantErrors.push({
            date: currentDate,
            message: errorMessage
        });
        try {
            window.localStorage.setItem(ERRORS_CACHE_KEY, JSON.stringify(relevantErrors));
        } catch (_) {}
    }
    return inCache;
};
const getLastCall = (event)=>{
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    if (!frames.length) return false;
    const lastFrame = frames[frames.length - 1];
    if (lastFrame?.in_app === false) return false;
    return lastFrame.function ? String(lastFrame.function) : false;
};
const setEventTags = (event, extraData, user, settings)=>{
    if (!event.tags) event.tags = {};
    event.tags.fromStatic = event.request?.url !== window.location.href;
    event.tags.breakpoint = __WEBPACK_EXTERNAL_MODULE__hh_ru_browser_api_utils_8f48670e__.Cookies.get('device_breakpoint') || __WEBPACK_EXTERNAL_MODULE__hh_ru_browser_api_utils_8f48670e__.Cookies.get('device_magritte_breakpoint');
    event.tags.pageName = String(extraData.pageName || NOT_DETECTED_TAG_VALUE);
    event.tags.userType = user.userType;
    const tagsWithDefault = [
        'device',
        'device.family',
        'browser',
        'browser.name',
        'breakpoint',
        'os',
        'os.name',
        'pageName'
    ];
    if (settings.isRootClient) {
        tagsWithDefault.push('failedAxiosUrl');
        tagsWithDefault.push('third_party_code');
    }
    tagsWithDefault.forEach((tagName)=>{
        if (event.tags && !(tagName in event.tags)) event.tags[tagName] = NOT_DETECTED_TAG_VALUE;
    });
};
const prepareConfig = ({ sentryJsConfig, sentryDSN, buildVersion, staticHost, beforeSendCallback, getUser, getExtraData, isDebug }, settings = {})=>{
    const parsedExternalConfig = parseSentryExternalConfig(sentryJsConfig);
    const mergedConfig = (0, __WEBPACK_EXTERNAL_MODULE_deepmerge__["default"])(__WEBPACK_EXTERNAL_MODULE__defaultConfig_mjs_d5104688__.DEFAULT_CONFIG, parsedExternalConfig ?? {});
    const ignoreErrors = getIgnoreConfig('ignoreErrors', mergedConfig);
    const denyUrls = getIgnoreConfig('ignorePaths', mergedConfig);
    const ignoreErrorsStacktrace = mergedConfig?.ignoreErrors?.stacktraces;
    const replaysOnErrorSampleRate = mergedConfig?.replaysOnErrorSampleRate ?? '0';
    const tracesSampleRate = mergedConfig?.tracesSampleRate ?? '0';
    const ignoreErrorsSerialize = mergedConfig?.ignoreErrors?.serialize;
    return {
        externalConfig: mergedConfig,
        clientConfig: {
            dsn: sentryDSN,
            release: buildVersion,
            ignoreErrors,
            denyUrls,
            includePaths: [
                staticHost,
                window.location.host,
                'webpack-internal'
            ],
            maxBreadcrumbs: 15,
            normalizeDepth: 8,
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: parseFloat(replaysOnErrorSampleRate),
            tracesSampleRate: parseFloat(tracesSampleRate),
            beforeSend: async (event, hint)=>{
                if (isDebug) {
                    console.error(`Calling beforeSend for ${buildVersion}`);
                    console.error('Hint in beforeSend', hint);
                    console.error('Event in beforeSend:start', event);
                }
                if (settings.isRootClient) await new Promise((resolve)=>setTimeout(resolve, 100));
                if (hint.originalException?.[ALREADY_HANDLED_FLAG]) {
                    isDebug && console.error('Exception already handled, skipping: ', hint.originalException);
                    return null;
                }
                if (null === document.body) return null;
                let url = event?.request?.url;
                let lineNumber = 0;
                let errorMessage = event.message;
                if (event.exception) {
                    const error = event.exception.values?.[0];
                    errorMessage = error?.value || 'Unknown error message';
                    if (error?.stacktrace) {
                        if (Array.isArray(ignoreErrorsStacktrace)) {
                            if (ignoreErrorsStacktrace.some((stacktraceString)=>{
                                const found = stacktraceString.match(/^(?<func>[^(]+)?(?:\(<(?<module>[^>]+)>\))?$/);
                                if (found?.groups) {
                                    const { func, module } = found.groups;
                                    if (func || module) return error.stacktrace?.frames?.some((frame)=>(func ? frame.function?.includes(func) : true) && (module ? frame.filename?.includes(module) : true));
                                }
                                return false;
                            })) return null;
                        }
                        const stacktrace = error.stacktrace.frames?.[error.stacktrace.frames.length - 1];
                        url = stacktrace?.filename;
                        lineNumber = stacktrace?.lineno || 0;
                        setFramesInAppFlag(error.stacktrace.frames);
                    }
                }
                const serialized = event.extra?.__serialized__;
                if ('object' == typeof serialized && null !== serialized && ignoreErrorsSerialize) {
                    if (ignoreErrorsSerialize.some((serializeObject)=>{
                        for (const [key, value] of Object.entries(serializeObject)){
                            const valueRegExp = new RegExp(value, 'i');
                            return Object.hasOwn(serialized, key) && valueRegExp.test(serialized[key]);
                        }
                        return false;
                    })) return null;
                }
                event = (0, __WEBPACK_EXTERNAL_MODULE__pushAxiosDataToSentry_mjs_7c7579fb__["default"])(event);
                const callbackResult = beforeSendCallback?.(event);
                if (false === callbackResult) return null;
                if (!isDebug && isErrorInCache(`${String(errorMessage)}|${String(url)}|${lineNumber}`)) return null;
                const user = getUser();
                event.user = user;
                if (!event.extra) event.extra = {};
                const extraData = getExtraData();
                event.extra = {
                    ...event.extra,
                    ...extraData,
                    userType: user.userType,
                    screenBreakpointBloko: __WEBPACK_EXTERNAL_MODULE__hh_ru_browser_api_utils_8f48670e__.Cookies.get('device_breakpoint'),
                    screenBreakpointMagritte: __WEBPACK_EXTERNAL_MODULE__hh_ru_browser_api_utils_8f48670e__.Cookies.get('device_magritte_breakpoint'),
                    documentStates: {
                        readyState: document.readyState,
                        visibilityState: document.visibilityState,
                        onLineState: window.navigator?.onLine
                    }
                };
                const reduxLastAction = event.extra.lastAction;
                if (reduxLastAction?.payload) {
                    const lastActionPayloadLength = (0, __WEBPACK_EXTERNAL_MODULE_json_stringify_safe_eb014bbd__["default"])(reduxLastAction.payload).length;
                    if (lastActionPayloadLength > MAX_LAST_ACTION_PAYLOAD_LENGTH) reduxLastAction.payload = '[removed]';
                }
                (0, __WEBPACK_EXTERNAL_MODULE__removeData_mjs_1c816fc9__.removeExtraData)(event);
                if (!event.fingerprint && event.exception?.values?.[0]?.value) event.fingerprint = [
                    event.exception.values[0].value
                ];
                const lastCall = getLastCall(event);
                if (event.fingerprint && lastCall) event.fingerprint.push(lastCall);
                setEventTags(event, extraData, user, settings);
                isDebug && console.error('Event in beforeSend:end', event);
                return event;
            },
            beforeBreadcrumb (breadcrumb) {
                if ('xhr' === breadcrumb.category) {
                    const url = breadcrumb.data?.url;
                    if (!url?.startsWith('/')) return null;
                }
                return breadcrumb;
            }
        }
    };
};
const utils_rslib_entry_ = {};
export { ALREADY_HANDLED_FLAG, utils_rslib_entry_ as default, getEnvironment, isBrowserSupported, prepareConfig };

//# sourceMappingURL=utils.mjs.map